import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { AHText, Box, useSpaceSize, useTheme } from "arkh";

import { TeamScheduleListItem } from "../TeamScheduleListItem/TeamScheduleListItem";

import { DateTime } from "luxon";
import {
  TeamScheduleSectionHeader,
  TeamScheduleSectionHeaderSummaryHeight,
} from "./TeamScheduleHeader/TeamScheduleSectionHeader";
import { TeamScheduleProps } from "./TeamSchedule";
import { TeamScheduleComponent_team$key } from "./__generated__/TeamScheduleComponent_team.graphql";
import { useNowIndexes } from "./useNowIndexes";

import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedScrollHandler,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";
import { FlashList, FlashListRef } from "@shopify/flash-list";

import { InteractionManager, Platform } from "react-native";
import { useBackgroundColor } from "./useBackgroundColor";
import { FormattedMessage } from "react-intl";

import { TeamScheduleStickyHeaders } from "./TeamScheduleStickyHeaders";
import { useTeamScheduleLayout } from "./useTeamScheduleLayout";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useNonReactiveCallback } from "@/src/common/useNonReactiveCallback";

function OccasionNumber(props: { children: React.ReactNode }) {
  const s = Array.isArray(props.children) ? props.children[0] : props.children;
  return (
    <AHText type="info-small" color={s === "0" ? "text-secondary" : "negative"}>
      {s}
    </AHText>
  );
}

export function TeamScheduleComponent(
  props: TeamScheduleProps & { team: TeamScheduleComponent_team$key }
) {
  const theme = useTheme();
  const team = useFragment(
    graphql`
      fragment TeamScheduleComponent_team on Team {
        color
        prevDailySchedule: currentTeamSchedule(freq: DAILY, movePeriod: -1) {
          id
          notCompletedCount
        }
        dailySchedule: currentTeamSchedule(freq: DAILY) {
          id
          isActive
          events(includeInlineRoutines: true) {
            id
            startAt
            ...useTeamScheduleLayout_teamScheduleEventOccasion
            ...useNowIndexes_teamScheduleEventOccasion
            ...TeamScheduleListItem_TeamScheduleEventOccasion
          }
          ...TeamScheduleSectionHeaderSummary_teamSchedule
          ...TeamScheduleSectionHeader_teamSchedule
        }
        prevWeeklySchedule: currentTeamSchedule(freq: WEEKLY, movePeriod: -1) {
          id
          notCompletedCount
        }
        weeklySchedule: currentTeamSchedule(freq: WEEKLY) {
          id
          isActive
          events(includeInlineRoutines: true) {
            id
            ...useTeamScheduleLayout_teamScheduleEventOccasion
            ...TeamScheduleListItem_TeamScheduleEventOccasion
          }
          ...TeamScheduleSectionHeaderSummary_teamSchedule
          ...TeamScheduleSectionHeader_teamSchedule
        }
        prevMonthlySchedule: currentTeamSchedule(
          freq: MONTHLY
          movePeriod: -1
        ) {
          id
          notCompletedCount
        }
        monthlySchedule: currentTeamSchedule(freq: MONTHLY) {
          id
          isActive
          events(includeInlineRoutines: true) {
            id
            ...useTeamScheduleLayout_teamScheduleEventOccasion
            ...TeamScheduleListItem_TeamScheduleEventOccasion
          }
          ...TeamScheduleSectionHeader_teamSchedule
          ...TeamScheduleSectionHeaderSummary_teamSchedule
        }
      }
    `,
    props.team
  );
  const intl_token = useIntlTokens();

  const monthlySection = useMemo(
    () =>
      team.monthlySchedule.isActive
        ? {
            numberOfItems: team.monthlySchedule.events.length,
            freq: "MONTHLY" as const,
            schedule: team.monthlySchedule,
            title: intl_token.MonthlyRoutines,
            events: [
              {
                type: "HEADER" as const,
                id: team.monthlySchedule.id,
                prevId: team.prevMonthlySchedule.id,
                schedule: team.monthlySchedule,
                title: intl_token.MonthlyRoutines,
                numberOfItems: team.monthlySchedule.events.length,
                freq: "MONTHLY" as const,
                historyTitle: (
                  <FormattedMessage
                    defaultMessage="<b>{missingOccasions}</b> missed last month"
                    description="TeamSchedule Monthly Routines Section Header Missing Occasions"
                    values={{
                      b: (chunks) => <OccasionNumber>{chunks}</OccasionNumber>,

                      missingOccasions:
                        team.prevMonthlySchedule.notCompletedCount,
                    }}
                  />
                ),
              },

              ...team.monthlySchedule.events.map((e) => ({
                type: "LISTITEM-monthly" as const,
                id: e.id,
                eventOccasion: e,
                numberOfItems: team.monthlySchedule.events.length,
              })),
            ],
          }
        : null,
    [intl_token.MonthlyRoutines, team.monthlySchedule, team.prevMonthlySchedule]
  );
  const weeklySection = useMemo(
    () =>
      team.weeklySchedule.isActive
        ? {
            numberOfItems: team.weeklySchedule.events.length,
            freq: "WEEKLY" as const,
            schedule: team.weeklySchedule,
            title: intl_token.WeeklyRoutines,
            events: [
              {
                type: "HEADER" as const,
                title: intl_token.WeeklyRoutines,
                id: team.weeklySchedule.id,
                prevId: team.prevWeeklySchedule.id,
                schedule: team.weeklySchedule,
                numberOfItems: team.weeklySchedule.events.length,
                freq: "WEEKLY" as const,
                historyTitle: (
                  <FormattedMessage
                    defaultMessage="<b>{missingOccasions}</b> missed last week"
                    description="TeamSchedule Weekly Routines Section Header Missing Occasions"
                    values={{
                      missingOccasions:
                        team.prevWeeklySchedule.notCompletedCount,
                      b: (chunks) => <OccasionNumber>{chunks}</OccasionNumber>,
                    }}
                  />
                ),
              },
              ...team.weeklySchedule.events.map((e) => ({
                type: "LISTITEM-weekly" as const,
                id: e.id,
                eventOccasion: e,
                numberOfItems: team.weeklySchedule.events.length,
              })),
            ],
          }
        : null,
    [intl_token.WeeklyRoutines, team.prevWeeklySchedule, team.weeklySchedule]
  );
  const dailySection = useMemo(
    () =>
      team.dailySchedule.isActive
        ? {
            numberOfItems: team.dailySchedule.events.length,
            freq: "DAILY" as const,
            schedule: team.dailySchedule,
            title: intl_token.DailyRoutines,
            events: [
              {
                type: "HEADER" as const,
                id: team.dailySchedule.id,
                prevId: team.prevDailySchedule.id,
                title: intl_token.DailyRoutines,

                schedule: team.dailySchedule,
                freq: "DAILY" as const,
                historyTitle: (
                  <FormattedMessage
                    defaultMessage="<b>{missingOccasions}</b> missed yesterday"
                    description="TeamSchedule Daily Routines Section Header Missing Occasions"
                    values={{
                      missingOccasions:
                        team.prevDailySchedule.notCompletedCount,
                      b: (chunks) => <OccasionNumber>{chunks}</OccasionNumber>,
                    }}
                  />
                ),
              },
              ...team.dailySchedule.events.map((e, index) => ({
                type: "LISTITEM-daily" as const,
                id: e.id,
                eventOccasion: e,
                index,
                numberOfItems: team.dailySchedule.events.length,
              })),
            ],
          }
        : null,

    [intl_token.DailyRoutines, team.dailySchedule, team.prevDailySchedule]
  );

  const backgroundColor = useBackgroundColor(team.color);

  const sections = [monthlySection, weeklySection, dailySection].flatMap((s) =>
    s ? [s] : []
  );
  const listData = useMemo(
    () => [
      ...(monthlySection?.events ?? []),
      ...(weeklySection?.events ?? []),
      ...(dailySection?.events ?? []),
    ],
    [dailySection?.events, monthlySection?.events, weeklySection?.events]
  );

  const { offsets: monthlyOffsets, layout: monthlyLayout } =
    useTeamScheduleLayout({
      isActive: team.monthlySchedule.isActive,
      teamScheduleEventOccasions: team.monthlySchedule?.events ?? [],
      freq: "MONTHLY",
      startY: 0,
      initialIndex: 0,
    });
  const { offsets: weeklyOffsets, layout: weeklyLayout } =
    useTeamScheduleLayout({
      isActive: team.weeklySchedule.isActive,
      teamScheduleEventOccasions: team.weeklySchedule?.events ?? [],
      freq: "WEEKLY",
      startY: monthlyLayout.endY,
      initialIndex: monthlyOffsets.length,
    });
  const { offsets: dailyOffsets, layout: dailyLayout } = useTeamScheduleLayout({
    isActive: team.dailySchedule.isActive,
    teamScheduleEventOccasions: team.dailySchedule?.events ?? [],
    freq: "DAILY",
    startY: weeklyLayout.endY,
    initialIndex: monthlyOffsets.length + weeklyOffsets.length,
  });
  const offsets = useMemo(() => {
    return [...monthlyOffsets, ...weeklyOffsets, ...dailyOffsets];
  }, [dailyOffsets, monthlyOffsets, weeklyOffsets]);
  const headerInfo = useMemo(() => {
    const map = new Map();
    if (monthlySection) {
      map.set("MONTHLY", monthlyLayout);
    }
    if (weeklySection) {
      map.set("WEEKLY", weeklyLayout);
    }
    if (dailySection) {
      map.set("DAILY", dailyLayout);
    }
    return map;
  }, [
    dailyLayout,
    dailySection,
    monthlyLayout,
    monthlySection,
    weeklyLayout,
    weeklySection,
  ]);
  const endIndexes = useMemo(() => {
    const set = new Set();
    if (monthlySection) {
      set.add(monthlyOffsets.length - 1);
    }
    if (weeklySection) {
      set.add(monthlyOffsets.length + weeklyOffsets.length - 1);
    }
    if (dailySection) {
      set.add(
        monthlyOffsets.length + weeklyOffsets.length + dailyOffsets.length - 1
      );
    }
    return set;
  }, [
    dailyOffsets.length,
    dailySection,
    monthlyOffsets.length,
    monthlySection,
    weeklyOffsets.length,
    weeklySection,
  ]);

  const now = DateTime.now();
  const [nowIndexes, scrollIndex] = useNowIndexes({
    now,
    events: team.dailySchedule.events,
  });

  const initialScrollIndex = useMemo(() => {
    if ((props.focusFrequency ?? "DAILY") === "DAILY") {
      const dailySection = headerInfo.get("DAILY");
      if (dailySection) {
        return dailySection.initialIndex;
      }
    } else {
      const section = headerInfo.get(props.focusFrequency ?? "MONTHLY");
      if (section) {
        return section.initialIndex;
      }
    }

    return undefined;
  }, [headerInfo, props.focusFrequency]);

  const y = useSharedValue(0);
  const listRef = useRef<FlashListRef<any>>(null);

  const { input, output } = useMemo(() => {
    const input: number[] = [-1];
    const output: number[] = [0];
    let numHeaders = 0;

    [...headerInfo.values()]
      .sort((a, b) => a.startY - b.startY)
      .forEach((v, i) => {
        input.push(v.startY - TeamScheduleSectionHeaderSummaryHeight * i);
        numHeaders++;
        output.push(numHeaders);
      });
    return { input, output };
  }, [headerInfo]);

  const numberOfStickyHeaders = useDerivedValue(() => {
    return interpolate(y.value, input, output, Extrapolation.CLAMP);
  });

  const scrollToFreq = useCallback(
    (freq: "DAILY" | "WEEKLY" | "MONTHLY", collapseIfCurrent = true) => {
      let target = headerInfo.get(freq)?.startY;
      if (target !== undefined && collapseIfCurrent) {
        const isCurrent = Math.abs(target - y.value) < 5;

        if (isCurrent) {
          switch (freq) {
            case "WEEKLY":
              target = headerInfo.get("DAILY")?.startY ?? target;
              break;
            case "MONTHLY":
              target =
                headerInfo.get("WEEKLY")?.startY ??
                headerInfo.get("DAILY")?.startY ??
                target;
              break;
            case "DAILY":
              target = dailyOffsets[scrollIndex];
              break;
          }
        }

        listRef.current?.scrollToOffset({
          offset: target, //Math.abs(target - y.value) > 5 ? target : secondaryTarget,
          animated: true,
        });
      }
    },
    [dailyOffsets, headerInfo, scrollIndex, y.value]
  );

  const scrollToFrequency = useNonReactiveCallback(
    (freq: typeof props.focusFrequency) => {
      if (!freq || freq === "DAILY") {
        const target = dailyOffsets[scrollIndex];

        if (target) {
          setTimeout(() => {
            InteractionManager.runAfterInteractions(() => {
              listRef.current?.scrollToOffset({
                offset: target,
                animated: true,
              });
            });
          }, 1000);
        }
      }
    }
  );
  useEffect(() => {
    scrollToFrequency(props.focusFrequency);
  }, [props.focusFrequency, scrollToFrequency]);

  const renderItem = useCallback(
    ({ item, index }: { item: (typeof listData)[number]; index: number }) => {
      const status = () => {
        if (item.type === "LISTITEM-daily") {
          if (nowIndexes.includes(item.index)) {
            return "active";
          }
          if (
            nowIndexes.includes(item.index - 1) &&
            DateTime.fromISO(item.eventOccasion.startAt) < now.plus({ hour: 1 })
          ) {
            return "soon";
          }
        }
        return "none";
      };
      switch (item.type) {
        case "HEADER":
          return (
            <TeamScheduleSectionHeader
              onPress={() => {
                scrollToFreq(item.freq, true);
              }}
              onPressHistory={() => {
                props.onOpenHistory(item.prevId, item.freq);
              }}
              teamSchedule={item.schedule}
              title={item.title}
              backgroundColor={backgroundColor}
              color={team.color ?? theme.primary}
              isFree={endIndexes.has(index)}
              historyTitle={item.historyTitle}
            />
          );
        case "LISTITEM-daily":
        case "LISTITEM-weekly":
        case "LISTITEM-monthly":
          return (
            <Box style={{ height: offsets[index] - offsets[index - 1] }}>
              <TeamScheduleListItem
                isFirst={false}
                isLast={endIndexes.has(index)}
                itemType={
                  item.type === "LISTITEM-monthly"
                    ? "MONTHLY"
                    : item.type === "LISTITEM-weekly"
                      ? "WEEKLY"
                      : "timeOfDay"
                }
                status={status()}
                teamColor={team.color ?? theme.primary}
                eventOccasion={item.eventOccasion}
              />
            </Box>
          );
      }
    },
    [
      backgroundColor,
      endIndexes,
      now,
      nowIndexes,
      offsets,
      props,
      scrollToFreq,
      team.color,
      theme.primary,
    ]
  );

  const scrollHandler = useAnimatedScrollHandler({
    onScroll(event, context) {
      y.value = event.contentOffset.y;
    },
  });

  const mediumPadding = useSpaceSize("medium");
  const safeAreaInsets = useSafeAreaInsets();
  const paddingBottom = mediumPadding;
  const extraRightPadding = Platform.OS === "web" ? mediumPadding : 0; // account for scrollbars when using flashlist

  const getItemLayout = useCallback(
    (data: typeof listData, index: number) => {
      return {
        length: offsets[index] - offsets[index - 1],
        offset: offsets[index],
        index,
      };
    },
    [offsets]
  );

  return (
    <Box
      style={{
        flex: 1,
        backgroundColor,
      }}
      spaceTop={props.paddingTop}
    >
      <Box
        style={{
          flex: 1,
          backgroundColor,
        }}
      >
        {Platform.OS === "web" ? (
          <Animated.FlatList
            data={listData}
            ListHeaderComponent={
              <TeamScheduleStickyHeaders
                backgroundColor={backgroundColor}
                numberOfButtons={numberOfStickyHeaders}
                headers={sections.map((s) => ({
                  freq: s.freq,
                  schedule: s.schedule,
                  title: s.title,
                }))}
                extraRightPadding={0}
                onPress={scrollToFreq}
              />
            }
            stickyHeaderIndices={[0]}
            onScroll={scrollHandler}
            scrollEventThrottle={30}
            ref={listRef as any}
            getItemLayout={(data, index) =>
              getItemLayout(data as typeof listData, index)
            }
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            contentContainerStyle={{
              paddingBottom,
            }}
          />
        ) : (
          <>
            <FlashList
              ref={listRef}
              onScroll={(e) => {
                y.value = e.nativeEvent.contentOffset.y;
              }}
              scrollEventThrottle={30}
              initialScrollIndex={initialScrollIndex}
              contentContainerStyle={{
                paddingBottom: paddingBottom + safeAreaInsets.bottom,
              }}
              data={listData}
              renderItem={renderItem}
              getItemType={(item) =>
                item.type + (item.type === "HEADER" ? item.freq : "")
              }
            />
            <TeamScheduleStickyHeaders
              backgroundColor={backgroundColor}
              numberOfButtons={numberOfStickyHeaders}
              headers={sections.map((s) => ({
                freq: s.freq,
                schedule: s.schedule,
                title: s.title,
              }))}
              extraRightPadding={extraRightPadding}
              onPress={scrollToFreq}
            />
          </>
        )}
      </Box>
    </Box>
  );
}
